import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/guides/palworld-tcg-sleeve-set-vol-2";
const TITLE = "Palworld TCG Sleeve Set Vol 2 Release & Card Checklist";
const DESCRIPTION =
  "Palworld Official Card Game Sleeve & Card Set Vol. 2: what is confirmed so far, the expected card checklist format, restock signals and where to buy the Vol. 1 set now.";

const FAQ: FaqItem[] = [
  {
    q: "Has Palworld Sleeve & Card Set Vol. 2 been announced?",
    a: "Vol. 2 has not been given an official release date. Bushiroad confirmed Sleeve & Card Set Vol. 1 with five cards and matching sleeves, and a numbered Vol. 1 implies a second volume, so this page tracks the announcement rather than claiming a date that does not exist yet.",
  },
  {
    q: "How many cards will Sleeve & Card Set Vol. 2 contain?",
    a: "Vol. 1 shipped five cards, each with a base printing and a parallel foil printing, alongside 60 matching card sleeves. Unless the format changes, expect the same structure for Vol. 2: five new promo printings plus sleeve art.",
  },
  {
    q: "Are Sleeve & Card Set promos tournament legal?",
    a: "Yes. Sleeve set promos carry printed English card codes like any other printing, so they are legal in constructed play. The parallel foil version is the same card as its base printing for deck-building purposes.",
  },
  {
    q: "How do I avoid paying scalper prices on a sleeve set?",
    a: "Sealed accessory runs spike at launch and settle within weeks. Watch the asking board rather than buying on announcement day, and compare it against the completed-sale average before ordering.",
  },
];

export const Route = createFileRoute("/guides/palworld-tcg-sleeve-set-vol-2")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Guides", "/guides"],
        ["Sleeve & Card Set Vol. 2", "/guides/palworld-tcg-sleeve-set-vol-2"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: SleeveVol2Page,
});

function SleeveVol2Page() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · accessory sets</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Official Palworld Sleeve &amp; Card Set Volume 2 Restock Tracker
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          There is no confirmed release date for Palworld Official Card Game Sleeve &amp; Card Set Vol. 2 yet. Vol. 1
          shipped five promo cards — each with a base and a parallel foil printing — plus 60 matching sleeves, and the
          numbering is the clearest signal that a second volume follows. This page tracks the announcement, the expected
          checklist format and the restock behaviour to expect when it lands.
        </p>
      </header>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">What we know so far</h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>Vol. 1 format: five cards, base plus parallel foil printing of each, and 60 sleeves.</li>
          <li>No Vol. 2 date, card list or product code has been published by Bushiroad.</li>
          <li>Accessory runs are printed in smaller quantities than boosters, so they sell out faster and rebound slower.</li>
          <li>Launch-week asking prices on sealed accessory product typically overshoot, then settle within two to four weeks.</li>
        </ul>
      </section>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Where to buy while Vol. 2 is unannounced</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Vol. 1 is still the set that actually exists, and its promos already trade as singles. Order sealed stock and
          singles side by side through our{" "}
          <Link to="/marketplace" className="text-primary hover:underline">
            Palworld TCG bulk orders platform
          </Link>{" "}
          instead of paying launch-window premiums.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          <li>
            <Link
              to="/guides/$slug"
              params={{ slug: "palworld-tcg-sleeve-card-set-vol-1" }}
              className="text-primary hover:underline"
            >
              Palworld TCG Sleeve &amp; Card Set Vol. 1 guide
            </Link>{" "}
            — the confirmed five cards and both printings of each.
          </li>
          <li>
            <Link to="/tcg-price-index" className="text-primary hover:underline">
              Palworld TCG price index market trends
            </Link>{" "}
            — whether promo printings are running ahead of the market.
          </li>
          <li>
            <Link to="/tcg-bulk-order" className="text-primary hover:underline">
              Palworld TCG bulk order tool
            </Link>{" "}
            — drop a list of card codes and check out in one pass.
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Sleeve &amp; Card Set Vol. 2 FAQ" />
    </div>
  );
}
